import React, { useState, useEffect, useCallback } from 'react';
import {
  getPushCampaignAnalytics,
  getPushCampaigns,
  getAudienceReachCount,
  createPushCampaign,
  cancelPushCampaign,
  deletePushCampaign,
  PushCampaignItem,
  PushAnalyticsData,
} from '../services/adminService';
import {
  Megaphone,
  Users,
  Sparkles,
  Send,
  Calendar,
  Clock,
  CheckCircle2,
  Smartphone,
  Search,
  RefreshCw,
  Trash2,
  XCircle,
  Copy,
  ExternalLink,
  Sliders,
  Zap,
  X,
} from 'lucide-react';
import {
  pageContainer,
  cardStyle,
  statsGrid,
  labelStyle,
  valueStyle,
  subTextStyle,
  thStyle,
  tdStyle,
  primaryBtn,
  secondaryBtn,
  inputStyle,
  selectStyle,
} from '../styles/theme';
import PageHeader from '../components/PageHeader';
import Toast from '../components/Toast';
import Paginator from '../components/Paginator';
import { useToast } from '../utils/useAdminPage';

// ── Target Audience Presets ──────────────────────────────────────────────────
const AUDIENCE_OPTIONS = [
  { id: 'ALL_USERS', label: 'All App Users (Customers)', desc: 'Broadcast to all registered and active customer devices' },
  { id: 'ALL_SCHOLARS', label: 'All Verified Scholars', desc: 'Broadcast to all onboarded scholar and service provider accounts' },
  { id: 'INACTIVE_7D', label: 'Inactive for 7+ Days (Retention)', desc: 'Re-engage customers with no app activity in the last 7 days' },
  { id: 'INACTIVE_14D', label: 'Inactive for 14+ Days (Win-Back)', desc: 'Lapsed user re-engagement with special blessings or discounts' },
  { id: 'INACTIVE_30D', label: 'Inactive for 30+ Days (Dormant)', desc: 'Target users dormant for over a month' },
  { id: 'CUSTOM_SEGMENT', label: 'Custom Segment (Religion / City)', desc: 'Target specific religious traditions, cities, or custom filters' },
];

const NOTIFICATION_TYPES = [
  { id: 'ANNOUNCEMENT', label: 'Announcement', color: '#2563eb', bg: '#eff6ff', icon: '📢' },
  { id: 'PROMOTION', label: 'Promotion / Offer', color: '#059669', bg: '#ecfdf5', icon: '🏷️' },
  { id: 'FESTIVAL', label: 'Festival / Muhurat', color: '#d97706', bg: '#fffbeb', icon: '🪔' },
  { id: 'RETENTION', label: 'User Retention', color: '#7c3aed', bg: '#f5f3ff', icon: '✨' },
  { id: 'SYSTEM', label: 'System Alert', color: '#dc2626', bg: '#fef2f2', icon: '🔔' },
];

const DEEP_LINK_PRESETS = [
  { label: 'Explore Services', value: '/HomeScreens/explore' },
  { label: 'Book a Scholar', value: '/BookingScreen/book' },
  { label: 'Notifications Inbox', value: '/HomeScreens/notifications' },
  { label: 'Festivals & Muhurats', value: '/cards/FestivalScreen' },
  { label: 'Top Rated Scholars', value: '/modals/ScholarListModal' },
  { label: 'Support & Helpdesk', value: '/HomeScreens/support' },
];

const RELIGIONS = [
  'ALL',
  'Hinduism',
  'Islam',
  'Christianity',
  'Sikhism',
  'Buddhism',
  'Jainism',
  'Other',
];

export const PushCampaigns: React.FC = () => {
  const { toast, showToast } = useToast();

  // ── States ─────────────────────────────────────────────────────────────────
  const [analytics, setAnalytics] = useState<PushAnalyticsData | null>(null);
  const [campaigns, setCampaigns] = useState<PushCampaignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modal / Composer State
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<'android' | 'ios'>('android');

  // Form fields
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formAudience, setFormAudience] = useState('ALL_USERS');
  const [formType, setFormType] = useState('ANNOUNCEMENT');
  const [formDeepLink, setFormDeepLink] = useState('/HomeScreens/explore');
  const [customDeepLink, setCustomDeepLink] = useState('');
  const [formReligion, setFormReligion] = useState('ALL');
  const [formCity, setFormCity] = useState('');
  const [sendOption, setSendOption] = useState<'NOW' | 'SCHEDULE'>('NOW');
  const [scheduledDateTime, setScheduledDateTime] = useState('');

  // Audience Reach Live Calculator
  const [audienceReach, setAudienceReach] = useState<{
    totalUsers: number;
    fcmTokenCount: number;
    expoTokenCount: number;
    reachableUsers: number;
  } | null>(null);
  const [calculatingReach, setCalculatingReach] = useState(false);

  // ── Data Fetching ──────────────────────────────────────────────────────────
  const fetchAnalytics = async () => {
    try {
      const data = await getPushCampaignAnalytics();
      setAnalytics(data);
    } catch (e: any) {
      console.error('Failed to load push analytics:', e);
    }
  };

  const fetchCampaignsList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPushCampaigns({
        page,
        limit: 10,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        search: searchQuery.trim() || undefined,
      });
      setCampaigns(res.items || []);
      setTotalPages(res.pagination?.totalPages || 1);
      setTotalCount(res.pagination?.total || 0);
    } catch (e: any) {
      showToast(e.message || 'Failed to fetch campaigns', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchQuery, showToast]);

  useEffect(() => {
    fetchAnalytics();
    fetchCampaignsList();
  }, [fetchCampaignsList]);

  // Estimate audience reach whenever audience or filters change
  useEffect(() => {
    if (!isComposerOpen) return;
    let isCancelled = false;

    const calculateReach = async () => {
      setCalculatingReach(true);
      try {
        const filters: any = {};
        if (formAudience === 'CUSTOM_SEGMENT') {
          if (formReligion !== 'ALL') filters.religion = formReligion;
          if (formCity.trim()) filters.city = formCity.trim();
        }
        const data = await getAudienceReachCount(formAudience, filters);
        if (!isCancelled) {
          setAudienceReach(data);
        }
      } catch (err) {
        console.error('Error estimating reach:', err);
      } finally {
        if (!isCancelled) setCalculatingReach(false);
      }
    };

    const timer = setTimeout(calculateReach, 300);
    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [isComposerOpen, formAudience, formReligion, formCity]);

  // ── Form Handlers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormTitle('');
    setFormBody('');
    setFormImageUrl('');
    setFormAudience('ALL_USERS');
    setFormType('ANNOUNCEMENT');
    setFormDeepLink('/HomeScreens/explore');
    setCustomDeepLink('');
    setFormReligion('ALL');
    setFormCity('');
    setSendOption('NOW');
    setScheduledDateTime('');
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      showToast('Please enter a notification title', 'error');
      return;
    }
    if (!formBody.trim()) {
      showToast('Please enter notification body text', 'error');
      return;
    }
    if (sendOption === 'SCHEDULE' && !scheduledDateTime) {
      showToast('Please select a scheduled date and time', 'error');
      return;
    }

    const effectiveDeepLink = formDeepLink === 'CUSTOM' ? customDeepLink.trim() : formDeepLink;
    const targetFilters: any = {};
    if (formAudience === 'CUSTOM_SEGMENT') {
      if (formReligion !== 'ALL') targetFilters.religion = formReligion;
      if (formCity.trim()) targetFilters.city = formCity.trim();
    }

    setSubmitting(true);
    try {
      await createPushCampaign({
        title: formTitle.trim(),
        body: formBody.trim(),
        imageUrl: formImageUrl.trim() || undefined,
        targetAudience: formAudience,
        targetFilters: Object.keys(targetFilters).length > 0 ? targetFilters : undefined,
        deepLink: effectiveDeepLink || undefined,
        type: formType,
        scheduledAt: sendOption === 'SCHEDULE' ? new Date(scheduledDateTime).toISOString() : null,
      });

      showToast(
        sendOption === 'SCHEDULE'
          ? 'Push campaign scheduled successfully!'
          : 'Push notification broadcast dispatched immediately!',
        'success'
      );
      setIsComposerOpen(false);
      resetForm();
      fetchAnalytics();
      fetchCampaignsList();
    } catch (err: any) {
      showToast(err.message || 'Failed to dispatch push campaign', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled push campaign?')) return;
    try {
      await cancelPushCampaign(id);
      showToast('Scheduled campaign cancelled', 'success');
      fetchCampaignsList();
      fetchAnalytics();
    } catch (err: any) {
      showToast(err.message || 'Failed to cancel campaign', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this campaign log from records?')) return;
    try {
      await deletePushCampaign(id);
      showToast('Campaign record removed', 'success');
      fetchCampaignsList();
      fetchAnalytics();
    } catch (err: any) {
      showToast(err.message || 'Failed to delete record', 'error');
    }
  };

  const handleDuplicate = (c: PushCampaignItem) => {
    setFormTitle(c.title);
    setFormBody(c.body);
    setFormImageUrl(c.imageUrl || '');
    setFormAudience(c.targetAudience);
    setFormType(c.type);
    setFormDeepLink(c.deepLink || '/HomeScreens/explore');
    setSendOption('NOW');
    setIsComposerOpen(true);
  };

  // Stat Cards Data
  const stats = [
    {
      label: 'Active Customer Tokens',
      val: analytics ? analytics.activeTokens.customers.toLocaleString() : '—',
      sub: 'FCM & Expo reachable',
      icon: Users,
      color: '#059669',
      bg: '#ecfdf5',
      borderColor: '#a7f3d0',
    },
    {
      label: 'Scholar Devices',
      val: analytics ? analytics.activeTokens.scholars.toLocaleString() : '—',
      sub: 'Verified service providers',
      icon: Smartphone,
      color: '#2563eb',
      bg: '#eff6ff',
      borderColor: '#bfdbfe',
    },
    {
      label: 'Campaigns Dispatched',
      val: analytics ? analytics.campaigns.sent.toLocaleString() : '—',
      sub: `${analytics?.campaigns.scheduled || 0} scheduled upcoming`,
      icon: Send,
      color: '#7c3aed',
      bg: '#f5f3ff',
      borderColor: '#ddd6fe',
    },
    {
      label: 'FCM Delivery Rate',
      val: analytics ? analytics.campaigns.deliveryRate : '100%',
      sub: `${analytics ? analytics.campaigns.totalDelivered.toLocaleString() : '0'} total delivered`,
      icon: Zap,
      color: '#d97706',
      bg: '#fffbeb',
      borderColor: '#fde68a',
    },
  ];

  return (
    <div style={pageContainer}>
      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <PageHeader
        breadcrumb="Marketing"
        title="Push Campaigns & User Retention"
        subtitle="Broadcast customized push notifications via Google FCM & Expo, and manage automated user retention workflows."
        extraButtons={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => {
                fetchAnalytics();
                fetchCampaignsList();
                showToast('Analytics refreshed', 'success');
              }}
              style={{
                ...secondaryBtn,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                padding: '8px 16px',
                borderRadius: 10,
              }}
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              onClick={() => {
                resetForm();
                setIsComposerOpen(true);
              }}
              style={{
                ...primaryBtn,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                padding: '8px 18px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                borderColor: '#059669',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
              }}
            >
              <Megaphone size={15} />
              + New Push Campaign
            </button>
          </div>
        }
      />

      {/* ── Analytics KPI Cards ──────────────────────────────────────────────── */}
      <div style={statsGrid}>
        {stats.map((st, i) => {
          const Icon = st.icon;
          return (
            <div
              key={i}
              style={{
                ...cardStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 22px',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
            >
              <div>
                <p style={{ ...labelStyle, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {st.label}
                </p>
                <h3 style={{ ...valueStyle, fontSize: 26, margin: '6px 0 4px' }}>{st.val}</h3>
                <p style={{ ...subTextStyle, color: st.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {st.sub}
                </p>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  backgroundColor: st.bg,
                  border: `1px solid ${st.borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: st.color,
                  flexShrink: 0,
                }}
              >
                <Icon size={22} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Automated Retention Status Banner ────────────────────────────────── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #064e3b 0%, #0f172a 60%, #134e4a 100%)',
          borderRadius: 16,
          padding: '22px 28px',
          color: '#ffffff',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          marginBottom: 28,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
          }}
        >
          <div style={{ maxWidth: 700 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 12px',
                borderRadius: 20,
                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                color: '#6ee7b7',
                fontSize: 11,
                fontWeight: 700,
                marginBottom: 10,
                letterSpacing: '0.03em',
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: '#34d399',
                  boxShadow: '0 0 8px #34d399',
                }}
              />
              Active 24/7 Automation Engine
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: '#ffffff', letterSpacing: '-0.02em' }}>
              Automated Retention & Re-engagement Workflows Active
            </h3>
            <p style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6, margin: 0 }}>
              The ecosystem automatically runs 7-day & 14-day inactivity sweeps, 3-hour pre-booking alerts, post-service review prompts, and cancelled slot re-engagement nudges.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(10px)',
                padding: '10px 18px',
                borderRadius: 12,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                textAlign: 'center',
              }}
            >
              <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, margin: 0, letterSpacing: '0.05em' }}>
                Inactivity Drip
              </p>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#34d399', margin: '4px 0 0' }}>Daily 10:30 AM</p>
            </div>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(10px)',
                padding: '10px 18px',
                borderRadius: 12,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                textAlign: 'center',
              }}
            >
              <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, margin: 0, letterSpacing: '0.05em' }}>
                Queue Health
              </p>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#34d399', margin: '4px 0 0' }}>Optimal (0 Backlog)</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Campaign Logs Table & Filters ────────────────────────────────────── */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        {/* Table Filter Bar */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid hsl(var(--border))',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            backgroundColor: 'hsl(var(--card))',
          }}
        >
          {/* Status Pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {['ALL', 'SENT', 'SCHEDULED', 'FAILED', 'CANCELLED'].map((st) => {
              const isActive = statusFilter === st;
              return (
                <button
                  key={st}
                  onClick={() => {
                    setStatusFilter(st);
                    setPage(1);
                  }}
                  style={{
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: isActive ? '1px solid hsl(var(--foreground))' : '1px solid hsl(var(--border))',
                    backgroundColor: isActive ? 'hsl(var(--foreground))' : 'hsl(var(--muted)/0.3)',
                    color: isActive ? 'hsl(var(--background))' : 'hsl(var(--muted-foreground))',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {st === 'ALL' ? 'All Campaigns' : st}
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div style={{ position: 'relative', minWidth: 260 }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'hsl(var(--muted-foreground))',
              }}
            />
            <input
              type="text"
              placeholder="Search campaigns by title..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              style={{
                ...inputStyle,
                paddingLeft: 36,
                paddingRight: 14,
                paddingTop: 8,
                paddingBottom: 8,
                fontSize: 13,
                borderRadius: 10,
                background: 'hsl(var(--card))',
              }}
            />
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
            <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: '#059669', marginBottom: 12 }} />
            <p style={{ fontSize: 13, fontWeight: 500 }}>Loading campaign logs...</p>
          </div>
        ) : campaigns.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
            <Megaphone size={40} color="#cbd5e1" style={{ marginBottom: 12 }} />
            <h4 style={{ fontSize: 16, fontWeight: 700, color: 'hsl(var(--foreground))', margin: '0 0 6px' }}>
              No campaigns found
            </h4>
            <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', maxWidth: 420, margin: '0 auto 18px' }}>
              Create your first push campaign to broadcast announcements or promotional updates to all app users.
            </p>
            <button
              onClick={() => {
                resetForm();
                setIsComposerOpen(true);
              }}
              style={{
                ...primaryBtn,
                fontSize: 13,
                padding: '8px 18px',
                borderRadius: 10,
                background: '#059669',
                borderColor: '#059669',
              }}
            >
              Create Campaign Now
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: 'hsl(var(--muted)/0.25)', borderBottom: '1px solid hsl(var(--border))' }}>
                  <th style={{ ...thStyle, padding: '14px 18px' }}>Campaign & Message</th>
                  <th style={thStyle}>Audience</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Delivery Rate</th>
                  <th style={thStyle}>Date / Scheduled</th>
                  <th style={{ ...thStyle, textAlign: 'right', paddingRight: 18 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const typeMeta = NOTIFICATION_TYPES.find((t) => t.id === c.type) || NOTIFICATION_TYPES[0];
                  const deliveryPercent =
                    c.totalTargeted > 0 ? ((c.successCount / c.totalTargeted) * 100).toFixed(0) : '100';

                  return (
                    <tr
                      key={c.id}
                      style={{
                        borderBottom: '1px solid hsl(var(--border))',
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Title & Preview */}
                      <td style={{ ...tdStyle, padding: '14px 18px', maxWidth: 280 }}>
                        <div style={{ fontWeight: 700, color: 'hsl(var(--foreground))', fontSize: 13 }}>
                          {c.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'hsl(var(--muted-foreground))',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginTop: 2,
                          }}
                        >
                          {c.body}
                        </div>
                        {c.deepLink && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 11,
                              color: '#047857',
                              backgroundColor: '#ecfdf5',
                              padding: '2px 8px',
                              borderRadius: 6,
                              fontFamily: 'monospace',
                              marginTop: 4,
                            }}
                          >
                            <ExternalLink size={10} />
                            {c.deepLink}
                          </span>
                        )}
                      </td>

                      {/* Audience */}
                      <td style={tdStyle}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor: 'hsl(var(--muted)/0.4)',
                            color: 'hsl(var(--foreground))',
                          }}
                        >
                          <Users size={12} color="hsl(var(--muted-foreground))" />
                          {c.targetAudience.replace(/_/g, ' ')}
                        </span>
                      </td>

                      {/* Category Tag */}
                      <td style={tdStyle}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 10px',
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            backgroundColor: typeMeta.bg,
                            color: typeMeta.color,
                            border: `1px solid ${typeMeta.color}30`,
                          }}
                        >
                          <span>{typeMeta.icon}</span>
                          {typeMeta.label}
                        </span>
                      </td>

                      {/* Status */}
                      <td style={tdStyle}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '4px 10px',
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            backgroundColor:
                              c.status === 'SENT'
                                ? '#ecfdf5'
                                : c.status === 'SCHEDULED'
                                ? '#fffbeb'
                                : c.status === 'SENDING'
                                ? '#eff6ff'
                                : c.status === 'CANCELLED'
                                ? 'hsl(var(--muted)/0.4)'
                                : '#fef2f2',
                            color:
                              c.status === 'SENT'
                                ? '#047857'
                                : c.status === 'SCHEDULED'
                                ? '#b45309'
                                : c.status === 'SENDING'
                                ? '#1d4ed8'
                                : c.status === 'CANCELLED'
                                ? 'hsl(var(--muted-foreground))'
                                : '#b91c1c',
                            border: `1px solid ${
                              c.status === 'SENT'
                                ? '#a7f3d0'
                                : c.status === 'SCHEDULED'
                                ? '#fde68a'
                                : c.status === 'SENDING'
                                ? '#bfdbfe'
                                : c.status === 'CANCELLED'
                                ? 'hsl(var(--border))'
                                : '#fecaca'
                            }`,
                          }}
                        >
                          {c.status === 'SENT' && <CheckCircle2 size={12} />}
                          {c.status === 'SCHEDULED' && <Clock size={12} />}
                          {c.status === 'CANCELLED' && <XCircle size={12} />}
                          {c.status}
                        </span>
                      </td>

                      {/* Delivery Stats */}
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'hsl(var(--foreground))' }}>
                          {c.successCount.toLocaleString()} / {c.totalTargeted.toLocaleString()}
                        </div>
                        <div style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                          {deliveryPercent}% delivered {c.failureCount > 0 && `(${c.failureCount} failed)`}
                        </div>
                      </td>

                      {/* Date */}
                      <td style={{ ...tdStyle, fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                        {c.sentAt ? (
                          <div>{new Date(c.sentAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        ) : c.scheduledAt ? (
                          <div style={{ color: '#b45309', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Calendar size={12} />
                            {new Date(c.scheduledAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        ) : (
                          <div>{new Date(c.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        )}
                        {c.createdBy?.name && (
                          <div style={{ fontSize: 10, color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>by {c.createdBy.name}</div>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                          <button
                            onClick={() => handleDuplicate(c)}
                            title="Duplicate / Resend"
                            style={{
                              padding: '6px 8px',
                              borderRadius: 6,
                              border: '1px solid hsl(var(--border))',
                              backgroundColor: 'hsl(var(--card))',
                              color: 'hsl(var(--foreground))',
                              cursor: 'pointer',
                            }}
                          >
                            <Copy size={13} />
                          </button>
                          {c.status === 'SCHEDULED' && (
                            <button
                              onClick={() => handleCancel(c.id)}
                              title="Cancel Scheduled Push"
                              style={{
                                padding: '6px 8px',
                                borderRadius: 6,
                                border: '1px solid #fde68a',
                                backgroundColor: '#fffbeb',
                                color: '#b45309',
                                cursor: 'pointer',
                              }}
                            >
                              <XCircle size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(c.id)}
                            title="Delete Record"
                            style={{
                              padding: '6px 8px',
                              borderRadius: 6,
                              border: '1px solid #fecaca',
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                              cursor: 'pointer',
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginator */}
        {totalPages > 1 && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid hsl(var(--border))' }}>
            <Paginator total={totalCount} page={page} pageSize={10} onPage={setPage} />
          </div>
        )}
      </div>

      {/* ── Composer Modal & Smartphone Mockup ───────────────────────────────── */}
      {isComposerOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(6px)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              ...cardStyle,
              maxWidth: 960,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              borderRadius: 20,
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '18px 24px',
                borderBottom: '1px solid hsl(var(--border))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'hsl(var(--muted)/0.2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    backgroundColor: '#059669',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Megaphone size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: 'hsl(var(--foreground))' }}>
                    Broadcast Push Notification
                  </h3>
                  <p style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', margin: '2px 0 0' }}>
                    Dispatch rich push alerts with FCM & Expo delivery fallback
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsComposerOpen(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid hsl(var(--border))',
                  backgroundColor: 'hsl(var(--card))',
                  color: 'hsl(var(--muted-foreground))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body: Form (Left) & Mobile Mockup (Right) */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr',
                overflowY: 'auto',
                padding: 24,
                gap: 24,
                flex: 1,
              }}
            >
              {/* Form Section */}
              <form onSubmit={handleCreateCampaign} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* 1. Target Audience */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    1. Target Audience
                  </label>
                  <select
                    value={formAudience}
                    onChange={(e) => setFormAudience(e.target.value)}
                    style={{ ...selectStyle, width: '100%', fontSize: 13, fontWeight: 600 }}
                  >
                    {AUDIENCE_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>

                  {/* Reach Estimator Box */}
                  <div
                    style={{
                      marginTop: 8,
                      padding: '10px 14px',
                      borderRadius: 10,
                      backgroundColor: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      color: '#065f46',
                      fontWeight: 600,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Sparkles size={14} color="#059669" />
                      Estimated Reach:
                    </span>
                    <span style={{ fontWeight: 800, color: '#047857' }}>
                      {calculatingReach
                        ? 'Calculating...'
                        : `${audienceReach?.reachableUsers || 0} active devices (${audienceReach?.fcmTokenCount || 0} FCM)`}
                    </span>
                  </div>
                </div>

                {/* Custom Segment Filters */}
                {formAudience === 'CUSTOM_SEGMENT' && (
                  <div
                    style={{
                      padding: 14,
                      backgroundColor: 'hsl(var(--muted)/0.3)',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <p style={{ fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Sliders size={13} color="#059669" />
                      Audience Segmentation Filters:
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                          Religion Tradition
                        </label>
                        <select
                          value={formReligion}
                          onChange={(e) => setFormReligion(e.target.value)}
                          style={{ ...selectStyle, width: '100%', fontSize: 12, padding: '6px 10px' }}
                        >
                          {RELIGIONS.map((r) => (
                            <option key={r} value={r}>
                              {r === 'ALL' ? 'All Traditions' : r}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}>
                          City / Region (Optional)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Mumbai, Delhi"
                          value={formCity}
                          onChange={(e) => setFormCity(e.target.value)}
                          style={{ ...inputStyle, fontSize: 12, padding: '6px 10px' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. Notification Type */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    2. Notification Category
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {NOTIFICATION_TYPES.map((t) => {
                      const isSelected = formType === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setFormType(t.id)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 700,
                            border: isSelected ? `2px solid ${t.color}` : '1px solid hsl(var(--border))',
                            backgroundColor: isSelected ? t.bg : 'hsl(var(--card))',
                            color: isSelected ? t.color : 'hsl(var(--muted-foreground))',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <span>{t.icon}</span>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 3. Title & Body */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        3. Message Title *
                      </label>
                      <span style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>{formTitle.length}/65</span>
                    </div>
                    <input
                      type="text"
                      maxLength={65}
                      placeholder="e.g. 🌟 Auspicious Navratri Blessings Await You"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      style={{ ...inputStyle, fontSize: 13, fontWeight: 600 }}
                      required
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Body Message *
                      </label>
                      <span style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>{formBody.length}/200</span>
                    </div>
                    <textarea
                      rows={3}
                      maxLength={200}
                      placeholder="e.g. Book verified Vedic priests and scholars for home pujas and spiritual consultations on Divinifyy."
                      value={formBody}
                      onChange={(e) => setFormBody(e.target.value)}
                      style={{ ...inputStyle, fontSize: 13, resize: 'none', lineHeight: 1.5 }}
                      required
                    />
                  </div>
                </div>

                {/* 4. Banner Image URL (Optional) */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    4. Banner Image URL (Optional)
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/banner.jpg"
                    value={formImageUrl}
                    onChange={(e) => setFormImageUrl(e.target.value)}
                    style={{ ...inputStyle, fontSize: 12, fontFamily: 'monospace' }}
                  />
                </div>

                {/* 5. Deep Link Routing */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    5. App Deep Link (On Tap)
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                    {DEEP_LINK_PRESETS.map((p) => {
                      const isSel = formDeepLink === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => {
                            setFormDeepLink(p.value);
                            setCustomDeepLink('');
                          }}
                          style={{
                            padding: '6px 10px',
                            borderRadius: 8,
                            fontSize: 11,
                            fontWeight: isSel ? 700 : 500,
                            textAlign: 'left',
                            border: isSel ? '1px solid #059669' : '1px solid hsl(var(--border))',
                            backgroundColor: isSel ? '#ecfdf5' : 'hsl(var(--card))',
                            color: isSel ? '#065f46' : 'hsl(var(--muted-foreground))',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>

                  <input
                    type="text"
                    placeholder="Or custom route: /scholar/123 or /services"
                    value={formDeepLink === 'CUSTOM' ? customDeepLink : formDeepLink}
                    onChange={(e) => {
                      setFormDeepLink('CUSTOM');
                      setCustomDeepLink(e.target.value);
                    }}
                    style={{ ...inputStyle, fontSize: 12, fontFamily: 'monospace' }}
                  />
                </div>

                {/* 6. Send Options (Immediate vs Scheduled) */}
                <div style={{ paddingTop: 10, borderTop: '1px solid hsl(var(--border))' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                      <input
                        type="radio"
                        name="sendOption"
                        checked={sendOption === 'NOW'}
                        onChange={() => setSendOption('NOW')}
                        style={{ accentColor: '#059669' }}
                      />
                      Send Immediately
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                      <input
                        type="radio"
                        name="sendOption"
                        checked={sendOption === 'SCHEDULE'}
                        onChange={() => setSendOption('SCHEDULE')}
                        style={{ accentColor: '#059669' }}
                      />
                      Schedule for Later
                    </label>
                  </div>

                  {sendOption === 'SCHEDULE' && (
                    <div
                      style={{
                        padding: 12,
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                        Select Scheduled Date & Time (IST):
                      </label>
                      <input
                        type="datetime-local"
                        value={scheduledDateTime}
                        onChange={(e) => setScheduledDateTime(e.target.value)}
                        style={{ ...inputStyle, fontSize: 12, fontFamily: 'monospace', backgroundColor: '#ffffff' }}
                        required={sendOption === 'SCHEDULE'}
                      />
                    </div>
                  )}
                </div>

                {/* Submit Action */}
                <div style={{ paddingTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setIsComposerOpen(false)}
                    style={{ ...secondaryBtn, fontSize: 13, padding: '8px 18px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      ...primaryBtn,
                      fontSize: 13,
                      padding: '8px 22px',
                      borderRadius: 10,
                      backgroundColor: '#059669',
                      borderColor: '#059669',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      opacity: submitting ? 0.6 : 1,
                    }}
                  >
                    {submitting ? (
                      <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : sendOption === 'SCHEDULE' ? (
                      <Calendar size={14} />
                    ) : (
                      <Send size={14} />
                    )}
                    {submitting
                      ? 'Processing...'
                      : sendOption === 'SCHEDULE'
                      ? 'Schedule Push'
                      : 'Send Push Broadcast Now'}
                  </button>
                </div>
              </form>

              {/* Mobile Preview Mockup (Right Column) */}
              <div
                style={{
                  backgroundColor: 'hsl(var(--muted)/0.25)',
                  borderRadius: 18,
                  padding: '24px 16px',
                  border: '1px solid hsl(var(--border))',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Platform Toggle */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    backgroundColor: 'hsl(var(--card))',
                    padding: 4,
                    borderRadius: 10,
                    border: '1px solid hsl(var(--border))',
                    marginBottom: 20,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setPreviewPlatform('android')}
                    style={{
                      padding: '5px 14px',
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: 'none',
                      backgroundColor: previewPlatform === 'android' ? 'hsl(var(--foreground))' : 'transparent',
                      color: previewPlatform === 'android' ? 'hsl(var(--background))' : 'hsl(var(--muted-foreground))',
                      cursor: 'pointer',
                    }}
                  >
                    Android Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewPlatform('ios')}
                    style={{
                      padding: '5px 14px',
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: 'none',
                      backgroundColor: previewPlatform === 'ios' ? 'hsl(var(--foreground))' : 'transparent',
                      color: previewPlatform === 'ios' ? 'hsl(var(--background))' : 'hsl(var(--muted-foreground))',
                      cursor: 'pointer',
                    }}
                  >
                    iOS Preview
                  </button>
                </div>

                {/* Smartphone Mockup Frame */}
                <div
                  style={{
                    width: 280,
                    backgroundColor: '#0f172a',
                    borderRadius: 36,
                    padding: '14px 12px',
                    boxShadow: '0 20px 45px rgba(0, 0, 0, 0.4)',
                    border: '4px solid #334155',
                    color: '#ffffff',
                    position: 'relative',
                  }}
                >
                  {/* Speaker Bar & Camera */}
                  <div
                    style={{
                      width: 70,
                      height: 14,
                      backgroundColor: '#020617',
                      borderRadius: 20,
                      margin: '0 auto 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1e293b' }} />
                  </div>

                  {/* Status Bar */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 10,
                      color: '#94a3b8',
                      padding: '0 6px',
                      marginBottom: 14,
                      fontWeight: 600,
                    }}
                  >
                    <span>10:30</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>5G</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* Push Notification Card */}
                  <div
                    style={{
                      borderRadius: 14,
                      padding: 12,
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      backgroundColor: previewPlatform === 'android' ? '#ffffff' : 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      color: '#0f172a',
                      border: '1px solid rgba(0,0,0,0.06)',
                    }}
                  >
                    {/* App Logo & Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 6,
                            backgroundColor: '#059669',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: 10,
                            fontWeight: 900,
                          }}
                        >
                          D
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#1e293b' }}>Divinifyy</span>
                      </div>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>now</span>
                    </div>

                    {/* Title & Body */}
                    <p style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', margin: '0 0 4px', lineHeight: 1.3 }}>
                      {formTitle || 'Notification Title Preview'}
                    </p>
                    <p style={{ fontSize: 11, color: '#475569', margin: 0, lineHeight: 1.4 }}>
                      {formBody || 'Notification body content will appear here on your customer devices.'}
                    </p>

                    {/* Banner Image Preview */}
                    {formImageUrl && (
                      <div
                        style={{
                          marginTop: 8,
                          borderRadius: 8,
                          overflow: 'hidden',
                          maxHeight: 100,
                          backgroundColor: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <img
                          src={formImageUrl}
                          alt="Banner"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Home Bar */}
                  <div
                    style={{
                      width: 90,
                      height: 3,
                      backgroundColor: '#475569',
                      borderRadius: 10,
                      margin: '22px auto 4px',
                    }}
                  />
                </div>

                <p style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', textAlign: 'center', marginTop: 14 }}>
                  Live status bar notification simulation
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast toast={toast} />
    </div>
  );
};

export default PushCampaigns;
